import { computed, type ComputedRef } from 'vue';
import useConfiguration from '@/composables/useConfiguration';
import { ChatStyle, Position, Variant, Widget, WidgetType } from '@keyvue/one-api-client';
import useWindowSize from '@/composables/useWindowSize';
import HeyMessageLeft from '@/assets/variants/hey-message-left.svg?component';
import HeyMessageRight from '@/assets/variants/hey-message-right.svg?component';
import HeyMessageTopLeft from '@/assets/variants/hey-message-top-left.svg?component';
import HeyMessageTopRight from '@/assets/variants/hey-message-top-right.svg?component';
import IconRound from '@/assets/variants/round.svg?component';
import IconSquare from '@/assets/variants/square.svg?component';
import IconRoundedSquare from '@/assets/variants/rounded-square.svg?component';
import IconCorneredCircleBottomLeft from '@/assets/variants/cornered-circle-bottom-left.svg?component';
import IconCorneredCircleLeft from '@/assets/variants/cornered-circle-left.svg?component';
import IconCorneredCircleBottomRight from '@/assets/variants/cornered-circle-bottom-right.svg?component';
import IconCorneredCircleRight from '@/assets/variants/cornered-circle-right.svg?component';
import IconSquareLeft from '@/assets/variants/square-left.svg?component';
import IconSquareRight from '@/assets/variants/square-right.svg?component';
import DelayedQuickOptions from '@/components/DelayedQuickOptions.vue';
import DelayedGreeting from '@/components/DelayedGreeting.vue';
import { useI18n } from 'vue-i18n';
import messages from '@/composables/useWidgetAppearance.messages.json';
import useAppBranding from '@/composables/useAppBranding';

export default function useWidgetAppearance() {
    const { t } = useI18n({ messages });
    const { assistent, isPreview } = useConfiguration();
    const { isMobile } = useWindowSize();
    const { hasHeylohaBranding } = useAppBranding();

    const getWidgetProperty = <T>(mobileKey: keyof Widget, desktopKey: keyof Widget): ComputedRef<T | null> => {
        return computed(() => {
            if (!assistent.value?.widget) {
                return null;
            }

            return assistent.value.widget[isMobile.value ? mobileKey : desktopKey] as T;
        });
    };

    const variantForIcon = getWidgetProperty<Variant>('mobileVariant', 'desktopVariant');
    const positionForIcon = getWidgetProperty<Position>('mobilePosition', 'desktopPosition');
    const typeForWidget = getWidgetProperty<WidgetType>('mobileType', 'desktopType');
    const chatStyle = getWidgetProperty<ChatStyle>('mobileChatStyle', 'desktopChatStyle');

    const isChatStyleFlat = computed(() => {
        if (!chatStyle.value) {
            return false;
        }

        return ChatStyle.FLAT.equals(chatStyle.value);
    });

    const isChatStyleModern = computed(() => {
        if (!chatStyle.value) {
            return false;
        }

        return ChatStyle.MODERN.equals(chatStyle.value);
    });

    const componentIcon = computed(() => {
        const variant = variantForIcon.value;
        if (!variant) {
            return null;
        }

        if (Variant.ROUND.equals(variant)) {
            if (hasHeylohaBranding()) {
                if (Position.LEFT.equals(positionForIcon.value)) {
                    return HeyMessageLeft;
                }

                if (Position.RIGHT.equals(positionForIcon.value)) {
                    return HeyMessageRight;
                }

                if (Position.TOP_LEFT.equals(positionForIcon.value)) {
                    return HeyMessageTopLeft;
                }

                if (Position.TOP_RIGHT.equals(positionForIcon.value)) {
                    return HeyMessageTopRight;
                }

                if (Position.BOTTOM_LEFT.equals(positionForIcon.value)) {
                    return HeyMessageLeft;
                }

                if (Position.BOTTOM_RIGHT.equals(positionForIcon.value)) {
                    return HeyMessageRight;
                }
            }
            return IconRound;
        }

        if (Variant.ROUNDED_SQUARE.equals(variant)) {
            return IconRoundedSquare;
        }

        if (Variant.CORNERED_CIRCLE.equals(variant)) {
            if (Position.LEFT.equals(positionForIcon.value)) {
                return IconCorneredCircleLeft;
            }

            if (Position.RIGHT.equals(positionForIcon.value)) {
                return IconCorneredCircleRight;
            }

            if (Position.TOP_LEFT.equals(positionForIcon.value)) {
                return IconCorneredCircleLeft;
            }

            if (Position.TOP_RIGHT.equals(positionForIcon.value)) {
                return IconCorneredCircleRight;
            }

            if (Position.BOTTOM_LEFT.equals(positionForIcon.value)) {
                return IconCorneredCircleBottomLeft;
            }

            if (Position.BOTTOM_RIGHT.equals(positionForIcon.value)) {
                return IconCorneredCircleBottomRight;
            }

            return IconCorneredCircleBottomRight;
        }

        if (Variant.SQUARE.equals(variant)) {
            if (Position.LEFT.equals(positionForIcon.value)) {
                return IconSquareLeft;
            }

            if (Position.RIGHT.equals(positionForIcon.value)) {
                return IconSquareRight;
            }

            if (Position.TOP_LEFT.equals(positionForIcon.value)) {
                return IconSquareLeft;
            }

            if (Position.TOP_RIGHT.equals(positionForIcon.value)) {
                return IconSquareRight;
            }

            if (Position.BOTTOM_LEFT.equals(positionForIcon.value)) {
                return IconSquare;
            }

            if (Position.BOTTOM_RIGHT.equals(positionForIcon.value)) {
                return IconSquare;
            }

            return IconSquare;
        }

        return null;
    });

    const componentForGreeting = computed(() => {
        if (assistent.value?.widget.showQuickOptionsInsteadOfGreeting) {
            return DelayedQuickOptions;
        }

        if (positionForIcon.value) {
            return DelayedGreeting;
        }

        return null;
    });

    const placeholderText = computed(() => {
        return isPreview.value
            ? t('placeholderPreview')
            : t('placeholder');
    });

    return {
        variantForIcon,
        positionForIcon,
        typeForWidget,
        componentIcon,
        componentForGreeting,
        placeholderText,
        chatStyle,
        isChatStyleFlat,
        isChatStyleModern,
    };
}
